import { PlatformLocation } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";

const APP_ROUTE_PREFIXES = new Set([
  "admin",
  "user",
  "marketing",
  "project",
  "onboarding",
  "telemetry-dashboard",
  "unauthorized",
]);

/** Config keys whose values are absolute backend/external service URLs. */
const BACKEND_URL_CONFIG_KEYS = new Set<keyof RuntimeConfig>([
  "apiUrl",
  "agenticUrl",
  "tcoApiUrl",
  "tcoPythonUrl",
  "reportUrl",
  "tdgUrl",
  "telemetryUrl",
  "promptManagerUrl",
  "keycloakUrl",
]);

/**
 * URL derivation rules: when an env supplies only `rootDomain`,
 * every service URL is built automatically using the known
 * subdomain/path convention.  Any key present in config.json
 * overrides the derived value (useful for non-standard envs).
 *
 * Pattern: https://<subdomain>.services.<rootDomain><path>
 * or:       https://<subdomain>.<rootDomain><path>
 */
const URL_DERIVATION_RULES: Array<{
  key: keyof RuntimeConfig;
  build: (domain: string, svc: string) => string;
}> = [
  { key: "apiUrl",           build: (d, s) => `https://accello-backend.${s ? s + "." : ""}${d}/backend/api/v1` },
  { key: "agenticUrl",       build: (d, s) => `https://agentic-orchestrator.${s ? s + "." : ""}${d}` },
  { key: "tcoApiUrl",        build: (d, s) => `https://tco-backend-springboot.${s ? s + "." : ""}${d}/api` },
  { key: "tcoPythonUrl",     build: (d, s) => `https://tco-backend-py.${s ? s + "." : ""}${d}/pythonapi` },
  { key: "tdgUrl",           build: (d, s) => `https://tdg.${s ? s + "." : ""}${d}` },
  { key: "telemetryUrl",     build: (d, s) => `https://accello-telemetry.${s ? s + "." : ""}${d}/backend/api/v1` },
  { key: "promptManagerUrl", build: (d, s) => `https://assure-prompt-manager.${s ? s + "." : ""}${d}` },
  { key: "reportUrl",        build: (d, s) => `https://accello360-framework-report.${s ? s + "." : ""}${d}/accello-report-ui#/` },
  { key: "keycloakUrl",      build: (d, _s) => `https://sso.${d}/auth` },
];

export interface NestedKeycloakConfig {
  url?: string;
  realm?: string;
  clientId?: string;
  onLoad?: string;
  checkLoginIframe?: string | boolean;
}

export interface RuntimeConfig {
  apiUrl: string;
  agenticUrl: string;
  tcoApiUrl: string;
  tcoPythonUrl: string;
  reportUrl: string;
  isDomainResolver: boolean;
  isTelemetryDashboardEnabled: boolean;
  tdgUrl: string;
  telemetryUrl: string;
  promptManagerUrl: string;
  applicationBasePath: string;
  rootDomain: string;
  servicesSubdomain: string;
  keycloakUrl: string;
  keycloak?: NestedKeycloakConfig;
}

const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  apiUrl: "",
  agenticUrl: "",
  tcoApiUrl: "",
  tcoPythonUrl: "",
  reportUrl: "",
  isDomainResolver: false,
  isTelemetryDashboardEnabled: false,
  tdgUrl: "",
  telemetryUrl: "",
  promptManagerUrl: "",
  applicationBasePath: "/",
  rootDomain: "",
  servicesSubdomain: "",   // absent from config.json → no prefix segment in URLs
  keycloakUrl: "",
};

@Injectable({
  providedIn: "root",
})
export class ConfigService {
  private config: RuntimeConfig = { ...DEFAULT_RUNTIME_CONFIG };
  private configLoaded = false;
  private loadPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private platformLocation: PlatformLocation
  ) {}

  /**
   * Loads assets/config/config.json at runtime (prefixed with APPLICATION_BASE_PATH).
   * Safe to call multiple times; returns the same in-flight/completed promise.
   *
   * Falls back to empty defaults only when the file is absent.
   */
  loadRuntimeConfig(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = this.fetchRuntimeConfig();
    }

    return this.loadPromise;
  }

  /** Resolves once runtime config has finished loading (or failed over to defaults). */
  whenReady(): Promise<void> {
    return this.loadRuntimeConfig();
  }

  isConfigLoaded(): boolean {
    return this.configLoaded;
  }

  private fetchRuntimeConfig(): Promise<void> {
    const configUrl = this.getRuntimeConfigUrl();

    return firstValueFrom(this.http.get<RuntimeConfig>(configUrl))
      .then((runtimeConfig: RuntimeConfig) => {
        this.config = this.coerceTypes({
          ...DEFAULT_RUNTIME_CONFIG,
          ...this.flattenRuntimeConfig(runtimeConfig),
        });
        this.configLoaded = true;
        this.applyAssetBasePathToDocument();
      })
      .catch((err) => {
        console.warn(
          "[ConfigService] Runtime config not found. Using defaults.",
          err
        );
        this.config = this.coerceTypes(this.config);
        this.configLoaded = true;
        this.applyAssetBasePathToDocument();
      });
  }

  /**
   * Build a URL for static UI assets under assets/.
   * Accepts `images/foo.svg`, `/assets/images/foo.svg`, or `../../../../assets/images/foo.svg`.
   */
  buildAssetUrl(assetPath: string | null | undefined): string {
    if (!assetPath) {
      return "";
    }

    const trimmed = assetPath.trim();
    if (
      /^https?:\/\//i.test(trimmed) ||
      trimmed.startsWith("data:") ||
      trimmed.startsWith("blob:")
    ) {
      return trimmed;
    }

    const assetsMarker = "assets/";
    let relativePath = trimmed;
    const assetsIndex = relativePath.lastIndexOf(assetsMarker);
    if (assetsIndex >= 0) {
      relativePath = relativePath.slice(assetsIndex + assetsMarker.length);
    } else {
      relativePath = relativePath.replace(/^\/+/, "");
    }

    return `${this.getApplicationBasePath()}${assetsMarker}${relativePath}`;
  }

  /**
   * Return a runtime config value by key.
   * Backend URL values are normalized to absolute https URLs so HttpClient
   * never treats them as relative to applicationBasePath.
   */
  getConfig<K extends keyof RuntimeConfig>(key: K): RuntimeConfig[K] {
    const value = this.config[key];
    if (BACKEND_URL_CONFIG_KEYS.has(key)) {
      return this.normalizeBackendUrl(value as string) as RuntimeConfig[K];
    }

    return value;
  }

  /** Base path prefix (e.g. `/` or `/assure/`). */
  getApplicationBasePath(): string {
    if (this.configLoaded && this.config.applicationBasePath) {
      return this.normalizeBasePath(this.config.applicationBasePath);
    }

    return this.resolveApplicationBasePath();
  }

  /**
   * Build an in-app route prefixed with APPLICATION_BASE_PATH.
   * Example: `admin/organization` -> `/assure/admin/organization`
   */
  buildAppPath(route: string = ""): string {
    const normalizedRoute = route.replace(/^\/+/, "");
    const basePath = this.getApplicationBasePath();

    if (!normalizedRoute) {
      return basePath;
    }

    return basePath === "/"
      ? `/${normalizedRoute}`
      : `${basePath}${normalizedRoute}`;
  }

  /** Build an absolute app URL including origin and APPLICATION_BASE_PATH. */
  buildAppUrl(route: string = ""): string {
    return `${window.location.origin}${this.buildAppPath(route)}`;
  }

  /**
   * Keycloak post-logout URL: origin + applicationBasePath.
   * Example: https://wileyuat.hive-impv.helio.virtusa.dev/assure/
   */
  getLogoutRedirectUrl(): string {
    const origin = window.location.origin.replace(/\/+$/, "");
    const basePath = this.getApplicationBasePath();
    if (!basePath || basePath === "/") {
      return `${origin}/`;
    }
    const path = basePath.startsWith("/") ? basePath : `/${basePath}`;
    return `${origin}${path.endsWith("/") ? path : `${path}/`}`;
  }

  /**
   * Resolve runtime config path using APPLICATION_BASE_PATH.
   * Prefer inferring from the current URL so post-login redirects like
   * `/assure/user/project` still load `/assure/assets/config/config.json`
   * even when <base href> is missing or still `/`.
   */
  private getRuntimeConfigUrl(): string {
    return `${this.resolveApplicationBasePath()}assets/config/config.json`;
  }

  private resolveApplicationBasePath(): string {
    const fromLocation = this.inferBasePathFromLocation();
    if (fromLocation) {
      return fromLocation;
    }

    const baseHref = this.platformLocation.getBaseHrefFromDOM();
    if (baseHref && baseHref !== "${APPLICATION_BASE_PATH}") {
      return this.normalizeBasePath(baseHref);
    }

    return "/";
  }

  /**
   * Derive `/assure/` from `/assure/user/project`.
   * Returns `/` when the app routes start at the domain root.
   */
  private inferBasePathFromLocation(): string | null {
    const segments = window.location.pathname.split("/").filter(Boolean);

    if (segments.length === 0) {
      return null;
    }

    if (APP_ROUTE_PREFIXES.has(segments[0])) {
      return "/";
    }

    if (segments.length > 1 && APP_ROUTE_PREFIXES.has(segments[1])) {
      return this.normalizeBasePath(`/${segments[0]}`);
    }

    if (segments.length === 1 && !APP_ROUTE_PREFIXES.has(segments[0])) {
      return this.normalizeBasePath(`/${segments[0]}`);
    }

    return null;
  }

  private normalizeBasePath(basePath: string): string {
    if (!basePath || basePath === "/") {
      return "/";
    }

    return `${basePath.replace(/\/+$/, "")}/`;
  }

  /**
   * envsubst writes every value as a plain string.
   * This helper converts known boolean and numeric fields back to their
   * correct JavaScript types after the JSON file is parsed.
   */
  private flattenRuntimeConfig(raw: RuntimeConfig): RuntimeConfig {
    /** hive-impv config.json nests SSO as `keycloak.url` instead of `keycloakUrl`. */
    const nestedUrl = raw?.keycloak?.url;
    const flattened: RuntimeConfig = {
      ...raw,
      keycloakUrl: raw?.keycloakUrl || nestedUrl || "",
    };
    return this.deriveUrlsFromRootDomain(flattened);
  }

  private deriveUrlsFromRootDomain(cfg: RuntimeConfig): RuntimeConfig {
    const domain = (cfg.rootDomain || "").trim();
    if (!domain) {
      return cfg;
    }

    // "" or absent from config.json → no infix segment (e.g. https://svc.domain/...)
    // "services" / "svc" → infix inserted (e.g. https://svc.services.domain/...)
    const svc = (cfg.servicesSubdomain ?? "").trim();

    const result = { ...cfg };
    for (const rule of URL_DERIVATION_RULES) {
      const current = (result[rule.key] as string) || "";
      // Only fill in when the key is blank or is still a raw envsubst placeholder
      if (!current || current.startsWith("${")) {
        (result as any)[rule.key] = rule.build(domain, svc);
      }
    }
    return result;
  }

  private coerceTypes(cfg: RuntimeConfig): RuntimeConfig {
    return {
      ...cfg,
      isDomainResolver: this.toBoolean(cfg.isDomainResolver),
      isTelemetryDashboardEnabled: this.toBoolean(cfg.isTelemetryDashboardEnabled),
      apiUrl: this.normalizeBackendUrl(cfg.apiUrl),
      agenticUrl: this.normalizeBackendUrl(cfg.agenticUrl),
      tcoApiUrl: this.normalizeBackendUrl(cfg.tcoApiUrl),
      tcoPythonUrl: this.normalizeBackendUrl(cfg.tcoPythonUrl),
      reportUrl: this.normalizeBackendUrl(cfg.reportUrl),
      tdgUrl: this.normalizeBackendUrl(cfg.tdgUrl),
      telemetryUrl: this.normalizeBackendUrl(cfg.telemetryUrl),
      keycloakUrl: this.normalizeBackendUrl(cfg.keycloakUrl || cfg.keycloak?.url || ""),
    };
  }

  /**
   * Ensure backend URLs are absolute so HttpClient does not resolve them
   * relative to the UI origin/applicationBasePath (e.g. /assure/).
   */
  private normalizeBackendUrl(url: string): string {
    if (!url || typeof url !== "string") {
      return url;
    }

    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    if (trimmed.startsWith("//")) {
      return `https:${trimmed}`;
    }

    return `https://${trimmed.replace(/^\/+/, "")}`;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === "boolean") return value;
    return String(value).toLowerCase() === "true";
  }

  /** Expose asset base path and common background URLs for SCSS via CSS variables. */
  private applyAssetBasePathToDocument(): void {
    const root = document.documentElement;
    const assetsBase = `${this.getApplicationBasePath()}assets/`;

    root.style.setProperty("--app-assets-base-path", assetsBase);
    root.style.setProperty(
      "--asset-url-search-icon",
      `url("${this.buildAssetUrl("images/virtusa-icons/search-icon.svg")}")`
    );
    root.style.setProperty(
      "--asset-url-overview-bg",
      `url("${this.buildAssetUrl("images/virtusa-icons/overview-bg.png")}")`
    );
  }
}
